import { useEffect, useMemo } from 'react'
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { useAuth } from './useAuth'
import { useOnboardingStatus } from '@/features/onboarding/useOnboardingStatus'

function parseHouseholdIdFromJwt(accessToken: string): string | null {
  try {
    const b64 = accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(b64))
    return payload.household_id ?? null
  } catch {
    return null
  }
}

export function AuthGuard() {
  const { session, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useRouterState({ select: (s) => s.location.pathname })

  const hasHousehold = useMemo(() => {
    if (!session?.access_token) return false
    return !!parseHouseholdIdFromJwt(session.access_token)
  }, [session])

  const { data: onboardingStatus, isLoading: onboardingLoading } = useOnboardingStatus(
    !isLoading && !!session && hasHousehold
  )

  useEffect(() => {
    if (isLoading) return
    if (!session) {
      navigate({ to: '/login' })
      return
    }
    if (hasHousehold && !onboardingLoading) {
      if (onboardingStatus?.completed === false && !location.startsWith('/onboarding')) {
        navigate({ to: '/onboarding' })
      } else if (onboardingStatus?.completed === true && location.startsWith('/onboarding')) {
        navigate({ to: '/home' })
      }
    }
  }, [session, isLoading, hasHousehold, onboardingStatus, onboardingLoading, navigate, location])

  if (isLoading || (hasHousehold && onboardingLoading)) return null
  if (!session) return null

  return <Outlet />
}
